import { useEffect, useState } from "react";
import "react-day-picker/style.css";
import { DayPicker, type DateRange } from "react-day-picker";
import { uk } from "react-day-picker/locale";
import { Calendar } from "lucide-react";
import {
    Box,
    Button,
    Icon,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
} from "@chakra-ui/react";

const parseDate = (s: string): Date | undefined => {
    if (!s) return undefined;
    const parts = s.split("-").map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return undefined;
    return new Date(parts[0], parts[1] - 1, parts[2]);
};

const toInput = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const getTargetMonth = (startDate: string): Date => {
    const parsed = parseDate(startDate);
    if (!parsed) {
        return new Date();
    }
    return new Date(parsed.getFullYear(), parsed.getMonth(), 1);
};

export function DateRangePicker({
                                    start,
                                    end,
                                    placeholder,
                                    onChange,
                                }: {
    start: string;
    end: string;
    placeholder: string;
    onChange: (start: string, end: string) => void;
}) {
    const parsedStart = parseDate(start);
    const parsedEnd = parseDate(end);
    const externalSelected: DateRange | undefined =
        parsedStart && parsedEnd ? { from: parsedStart, to: parsedEnd } : undefined;

    // Local, in-progress selection. DayPicker needs to see the "from-only"
    // click immediately, before the parent commits a full range — otherwise
    // every second click looks like the start of a brand-new range.
    const [range, setRange] = useState<DateRange | undefined>(externalSelected);
    const [month, setMonth] = useState<Date>(() => getTargetMonth(start));

    // Keep in sync if the parent resets start/end externally (e.g. switching
    // the period dropdown away from "custom" and back).
    useEffect(() => {
        setRange(externalSelected);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [start, end]);

    useEffect(() => {
        if (start) {
            setMonth(getTargetMonth(start));
        }
    }, [start]);

    const label = start && end ? `${start} – ${end}` : placeholder;

    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);

    const startMonth = new Date(new Date().getFullYear() - 10, 0);
    const endMonth = new Date();

    return (
        <Popover placement="bottom-start" isLazy>
            <PopoverTrigger>
                <Button
                    size="sm"
                    variant="outline"
                    minW="220px"
                    justifyContent="flex-start"
                    leftIcon={<Icon as={Calendar} boxSize={4} />}
                >
                    {label}
                </Button>
            </PopoverTrigger>
            <Portal>
                <PopoverContent w="auto">
                    <PopoverArrow />
                    <PopoverBody>
                        <Box
                            sx={{
                                "--rdp-accent-color": "#FF7C49",
                                "--rdp-accent-background-color": "#FFE7DC",
                                "& .rdp-month_caption": {
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                },
                                "& .rdp-dropdowns": {
                                    justifyContent: "center",
                                    gap: "6px",
                                },
                                "& .rdp-dropdown_root": {
                                    cursor: "pointer",
                                    position: "relative",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "2px 6px",
                                    borderRadius: "md",
                                    transition: "all 0.15s ease",
                                    "&:hover": {
                                        bg: "blackAlpha.50",
                                    },
                                    "&:focus-within": {
                                        bg: "blackAlpha.50",
                                        boxShadow: "0 0 0 1.5px #FF7C49",
                                    },
                                },
                                "& .rdp-dropdown": {
                                    cursor: "pointer",
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    opacity: 0,
                                    outline: "none",
                                    border: "none",
                                    boxShadow: "none",
                                    textAlign: "center",
                                    textAlignLast: "center",
                                },
                                "& .rdp-dropdown:focus, & .rdp-dropdown:focus-visible": {
                                    outline: "none",
                                    border: "none",
                                    boxShadow: "none",
                                },
                                "& .rdp-dropdown:focus-visible ~ .rdp-caption_label": {
                                    outline: "none",
                                },
                                "& .rdp-dropdown option": {
                                    textAlign: "center",
                                },
                                "& .rdp-dropdown_root:hover .rdp-caption_label": {
                                    color: "#FF7C49",
                                },
                                "& .rdp-caption_label": {
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "4px",
                                    textAlign: "center",
                                    userSelect: "none",
                                },
                            }}
                        >
                            <DayPicker
                                mode="range"
                                numberOfMonths={1}
                                selected={range}
                                disabled={{ after: todayEnd }}
                                startMonth={startMonth}
                                endMonth={endMonth}
                                month={month}
                                onMonthChange={setMonth}
                                captionLayout="dropdown"
                                navLayout="around"
                                // @ts-expect-error — nested date-fns type mismatch (root date-fns@2 vs react-day-picker's internal date-fns@4); works fine at runtime
                                locale={uk}
                                onSelect={(r) => {
                                    setRange(r);
                                    if (r?.from && r?.to) onChange(toInput(r.from), toInput(r.to));
                                }}
                            />
                        </Box>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
}